import React from "react";
import clsx from "clsx";

import {
  IAssessmentContent,
  NEXT_ACTIONS_PAGE,
  SCORE_LABELS,
} from "../../../constants/assessment-content";
import AssessmentComment from "../AssessmentComment";
import AssessmentDistribution from "../AssessmentDistribution";
import {
  IReasonExperience,
  INextAction,
  IAssessmentExperienceTeacher,
  IAssessmentDistribution,
} from "../../../state";
import { ReactComponent as Talk1Icon } from "../../../assets/svg/glide_talk_1.svg";
import { ReactComponent as Talk2Icon } from "../../../assets/svg/glide_talk_2.svg";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/glide_arrow.svg";
import { NextActionKinds } from "../../../constants/enum";

import styles from "./AssessmentContent.module.scss";

export type Props = {
  hasComment?: boolean;
  distribution?: IAssessmentDistribution;
  content: IAssessmentContent;
  assessmentExperience: IAssessmentExperienceTeacher | null;
  assessmentNextActions?: INextAction[];
};

const AssessmentContent: React.FC<Props> = ({
  hasComment = true,
  distribution,
  content,
  assessmentExperience,
  assessmentNextActions,
}) => {
  const reasons: IReasonExperience[] = React.useMemo(() => {
    return assessmentExperience?.reasons || [];
  }, [assessmentExperience]);

  const filteredNextActions = React.useMemo(() => {
    const res: { [key: number]: IReasonExperience[] } = {};
    NextActionKinds.forEach(k => {
      res[k.kind] =
        assessmentNextActions
          ?.filter(item => item.kind === k.kind)
          .map(item => ({
            id: item.id,
            created_at: item.created_at,
            reason: item.next_action,
          })) ?? [];
    });
    return res;
  }, [assessmentNextActions]);

  return (
    <div className={clsx(styles.wrapper, styles[`wrapper--${content.id}`])}>
      <div className={styles.questionContent}>
        <h6 className={styles.title}>{content.titleForTeacherGlide}</h6>
        <h3>
          {content.questionTitle}
          {content.subQuestionTitle && <span>{content.subQuestionTitle}</span>}
        </h3>
        <p className={styles.textContent}>
          {content.questionContentForTeacher}
        </p>
        {content.personA && (
          <div className={styles.conversations}>
            <div className={styles.conversations__personA}>
              {content.iconGlide1 && (
                <img src={content.iconGlide1} alt="person A" />
              )}
            </div>
            <div className={styles.conversations__conversation}>
              <p>この素養がないと…</p>
              <div className="position-relative">
                <Talk1Icon />
                <span>{content.personA}</span>
              </div>
            </div>
            <div className={styles.conversations__arrow}>
              <ArrowIcon />
            </div>
            <div
              className={clsx(
                styles.conversations__conversation,
                styles[`conversations__conversation--b-${content.id}`],
              )}
            >
              <p>この素養があると…</p>
              <div className="position-relative">
                <Talk2Icon />
                <span>{content.personB}</span>
              </div>
            </div>
            <div className={styles.conversations__personB}>
              {content.iconGlide2 && (
                <img src={content.iconGlide2} alt="person B" />
              )}
            </div>
          </div>
        )}
      </div>
      <div className={styles.body}>
        {content.id !== NEXT_ACTIONS_PAGE.id && (
          <>
            <div className={styles.answers}>
              <div className={styles[`answers__${content.id}`]}>
                {Array.from(Array(4).keys()).map(idx => (
                  <div className={styles.answers__item} key={idx}>
                    <div
                      className={clsx(
                        assessmentExperience?.score &&
                          Math.floor(assessmentExperience.score) === idx + 1 &&
                          styles.active,
                      )}
                    >
                      <p>{(SCORE_LABELS as any)[`label${idx + 1}`]}</p>
                      {(content as any)[`score${idx + 1}`]}
                    </div>
                    {idx + 1 !== 4 && <span />}
                  </div>
                ))}
              </div>
              <div
                className={clsx(
                  styles[`answers__${content.id}`],
                  "mt-4 ms-auto",
                )}
              >
                {Array.from(Array(4).keys()).map(idx => (
                  <div className={styles.answers__item} key={idx + 5}>
                    <div
                      className={clsx(
                        assessmentExperience?.score &&
                          Math.floor(assessmentExperience.score) === idx + 5 &&
                          styles.active,
                      )}
                    >
                      <p>{(SCORE_LABELS as any)[`label${idx + 5}`]}</p>
                      {(content as any)[`score${idx + 5}`]}
                    </div>
                    {idx + 5 !== 8 && <span />}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {hasComment && content.id !== NEXT_ACTIONS_PAGE.id && (
          <div>
            <p className={clsx(styles.titleComment)}>
              生徒の入力内容（選んだ選択肢に対する具体的な場面や経験。また、この素養についての「気づいたこと」や「できたこと」）
            </p>
            <AssessmentComment reasons={reasons} />
            <div className="mb-4" />
          </div>
        )}
        {hasComment && content.id === NEXT_ACTIONS_PAGE.id && (
          <>
            {NextActionKinds.map(k => (
              <div key={k.kind}>
                <p className={clsx(styles.titleComment)}>{k.title}</p>
                <AssessmentComment reasons={filteredNextActions[k.kind]} />
                <div className="mb-4" />
              </div>
            ))}
          </>
        )}
        {distribution && (
          <AssessmentDistribution
            contentId={content.id}
            distribution={distribution}
          />
        )}
      </div>
    </div>
  );
};

export default AssessmentContent;
